import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/backend.config';
import type { ApiError, AuthResponse } from '../types';

/**
 * The access token lives in memory only — it is never written to localStorage, so an XSS payload
 * cannot lift a long-lived credential out of storage. The refresh token is kept in sessionStorage
 * so a page reload can recover the session; it dies with the tab.
 */
let accessToken: string | null = null;

const REFRESH_KEY = 'ims.refreshToken';

export const tokenStore = {
  get access() {
    return accessToken;
  },
  get refresh() {
    return sessionStorage.getItem(REFRESH_KEY);
  },
  set(tokens: { accessToken: string; refreshToken: string }) {
    accessToken = tokens.accessToken;
    sessionStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  },
  clear() {
    accessToken = null;
    sessionStorage.removeItem(REFRESH_KEY);
  },
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/** Callback wired up by AuthProvider so a failed refresh can drop the user back to /login. */
let onSessionExpired: (() => void) | null = null;
export const setSessionExpiredHandler = (fn: () => void) => {
  onSessionExpired = fn;
};

// Concurrent 401s share a single refresh round-trip.
let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) throw new Error('No refresh token');

  const { data } = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken ?? refreshToken });
  return data.accessToken;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    const isAuthCall = original?.url?.includes('/auth/');
    if (error.response?.status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      try {
        refreshInFlight = refreshInFlight ?? refreshAccessToken();
        const token = await refreshInFlight;
        refreshInFlight = null;
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        refreshInFlight = null;
        tokenStore.clear();
        onSessionExpired?.();
      }
    }
    return Promise.reject(error);
  },
);

/** Pulls the human-readable message out of the API's { status, message, errors } envelope. */
export function errorMessage(error: unknown): string {
  const axiosError = error as AxiosError<ApiError>;
  const data = axiosError?.response?.data;
  if (data?.errors) {
    const first = Object.values(data.errors)[0];
    if (first?.length) return first[0];
  }
  return data?.message ?? axiosError?.message ?? 'Something went wrong.';
}

/** Downloads a PDF/Excel response as a file, honouring the server's Content-Disposition name. */
export async function downloadFile(url: string, fallbackName: string) {
  // Receipts and reports render in whichever language the UI is showing, so a PDF opened from an
  // Urdu screen comes back in Urdu. Read from localStorage rather than a hook: this is called from
  // click handlers all over the app, not from inside a component.
  const language = localStorage.getItem('ims.language')?.startsWith('ur') ? 'ur' : 'en';
  const separator = url.includes('?') ? '&' : '?';
  const response = await api.get(`${url}${separator}lang=${language}`, { responseType: 'blob' });

  const disposition = response.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const fileName = match ? decodeURIComponent(match[1]) : fallbackName;

  const href = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
